import { type ReactNode, useRef, useState } from "react";

import { ValidationErrorText } from "@vega-core/components/Form/Layout/ValidationError";
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsContent,
  ComboboxChipsInput,
  ComboboxClear,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxLeftLabel,
  ComboboxList,
  type ComboboxProps,
  ComboboxValue,
} from "@vega-core/components/shadcn/combobox";
import { m } from "@vega-core/paraglide/messages";

import { DraggableComboboxChip } from "./components/DraggableComboboxChip";
import { DropableComboboxChipsInput } from "./components/DropableComboboxChipsInput";
import { useCreatable } from "./hooks/useCreatable";
import { normalizeSelectedItems } from "./helpers";
import { type SearchMultiSelectItem } from "./types";

export type SearchMultiselectProps<
  Value extends SearchMultiSelectItem,
  Multiple extends boolean | undefined = false,
> = {
  items: ComboboxProps<Value, Multiple>["items"];
  selectedValues: ComboboxProps<Value, Multiple>["value"];
  onSearchChange?: (value: string) => void;
  onSelectionChange?: ComboboxProps<Value, Multiple>["onValueChange"];
  renderItem?: (item: Value, inputValue: string) => ReactNode;
  renderLoader?: () => ReactNode;
  leftLabel?: string;
  leftLabelClassName?: string;
  error?: string;
  name: string;
  multiple?: Multiple;
  label?: ReactNode | string | null;
  hint?: string;
  draggable?: boolean;
  isLoading?: boolean;
  searchInputValue?: string;
  openOnInputClick?: boolean;
  minSearchLength?: number;
  disabled?: boolean;
  disableSelectedOptions?: boolean;
  autoHighlight?: ComboboxProps<Value, Multiple>["autoHighlight"];
  creatable?: boolean;
  className?: string;
};

/**
 * Improvement over SearchAutocomplete with multiselect functionality. Uses Combobox component from base-ui.
 * This component is fully controlled by the parent component.
 */
export function SearchMultiselect<
  Value extends SearchMultiSelectItem,
  Multiple extends boolean | undefined = false,
>({
  items,
  selectedValues,
  onSearchChange,
  onSelectionChange,
  renderItem,
  renderLoader,
  leftLabel,
  leftLabelClassName,
  error,
  name,
  multiple,
  hint,
  draggable = false,
  isLoading = false,
  searchInputValue,
  openOnInputClick = false,
  minSearchLength = 0,
  disabled = false,
  disableSelectedOptions = false,
  autoHighlight,
  creatable = false,
  className,
}: SearchMultiselectProps<Value, Multiple>) {
  const capturedInputValue = useRef("");
  const [typedInputValue, setTypedInputValue] = useState("");

  const inputId = `${name}-input`;
  const effectiveInputValue = typedInputValue || searchInputValue || "";
  const trimmedInputValue = effectiveInputValue.trim();
  const { createOption, renderedItems, handleSelectionChange } = useCreatable({
    items,
    selectedValues,
    trimmedInputValue,
    creatable,
    multiple,
    onSelectionChange,
  });
  const isBelowMinSearch = trimmedInputValue.length < minSearchLength;

  const selectedIds = new Set(
    normalizeSelectedItems(selectedValues).map((selected) => selected.id),
  );
  const isSelectedOption = (item: Value) =>
    disableSelectedOptions && selectedIds.has(item.id);

  const matchesInput = (item: Value, inputValue: string) =>
    item.searchValue.toLowerCase().includes(inputValue.trim().toLowerCase());

  const hasVisibleMatches = renderedItems.some((item) =>
    matchesInput(item, trimmedInputValue),
  );

  const renderDropdownItemLabel = (item: Value) => {
    if (createOption && item.id === createOption.id) {
      return `Create "${createOption.id}"`;
    }

    if (renderItem) {
      return renderItem(item, capturedInputValue.current);
    }

    return item.label;
  };

  const renderDropdownContent = () => {
    if (isBelowMinSearch) {
      return (
        <div
          className="px-3 py-2 text-text/secondary body-small-regular"
          data-testid={`${name}-search-multiselect-min-search-hint`}
        >
          {m.common_searchMinCharsHint({ count: minSearchLength })}
        </div>
      );
    }

    if (isLoading && renderLoader && !hasVisibleMatches) {
      return renderLoader();
    }

    return (
      <>
        {isLoading ? null : (
          <ComboboxEmpty>{m.common_noResultsFound()}</ComboboxEmpty>
        )}
        <ComboboxList data-testid={`${name}-search-multiselect-listbox`}>
          {(item: Value) => (
            <ComboboxItem
              className="data-[disabled]:pointer-events-none data-[highlighted]:bg-background/tertiary/50"
              disabled={isSelectedOption(item)}
              key={item.id}
              value={item}
            >
              {renderDropdownItemLabel(item)}
            </ComboboxItem>
          )}
        </ComboboxList>
      </>
    );
  };

  return (
    <Combobox
      autoHighlight={autoHighlight}
      disabled={disabled}
      filter={matchesInput}
      inputValue={searchInputValue}
      isItemEqualToValue={(item, value) => item.id === value.id}
      items={renderedItems}
      multiple={multiple}
      onInputValueChange={(inputValue) => {
        setTypedInputValue(inputValue);
        capturedInputValue.current = inputValue;
        onSearchChange?.(inputValue);
      }}
      onValueChange={(value, eventDetails) => {
        if (eventDetails.reason === "escape-key") {
          eventDetails.allowPropagation();

          return;
        }

        handleSelectionChange(value, eventDetails);
      }}
      openOnInputClick={openOnInputClick}
      value={selectedValues}
    >
      <div>
        <ComboboxChips
          className={className}
          data-testid={`${name}-search-multiselect-combobox-chips`}
          disabled={disabled}
          hasError={!!error}
        >
          {leftLabel ? (
            <ComboboxLeftLabel className={leftLabelClassName} htmlFor={inputId}>
              {leftLabel}
            </ComboboxLeftLabel>
          ) : null}
          <ComboboxChipsContent>
            {multiple ? (
              <ComboboxValue>
                {(values: ComboboxProps<Value, true>["value"]) =>
                  values?.map((item) =>
                    draggable ? (
                      <DraggableComboboxChip
                        fieldName={name}
                        item={item}
                        key={item.id}
                      >
                        <span>{item.label}</span>
                      </DraggableComboboxChip>
                    ) : (
                      <ComboboxChip key={item.id}>
                        <span>{item.label}</span>
                      </ComboboxChip>
                    ),
                  )
                }
              </ComboboxValue>
            ) : null}
            {draggable && multiple && Array.isArray(selectedValues) ? (
              <DropableComboboxChipsInput
                disabled={disabled}
                fieldName={name}
                id={inputId}
                item={selectedValues.at(-1)}
              />
            ) : (
              <ComboboxChipsInput disabled={disabled} id={inputId} />
            )}
            {multiple ? null : <ComboboxClear />}
          </ComboboxChipsContent>
        </ComboboxChips>
        {/* Hint should be put below the combobox when left label is present */}
        {leftLabel && hint ? (
          <span className="pl-2 text-text/secondary body-small-regular">
            {hint}
          </span>
        ) : null}
        {error ? (
          <ValidationErrorText dataTestId="searchMultiselect" withMargin>
            {error}
          </ValidationErrorText>
        ) : null}
      </div>

      <ComboboxContent>{renderDropdownContent()}</ComboboxContent>
    </Combobox>
  );
}
